import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const SESSION_KEY = "mo_analytics_session_id";
const SESSION_STARTED_KEY = "mo_analytics_session_started";
const PREVIOUS_SECTION_KEY = "mo_analytics_previous_section";
const LAST_VISIT_KEY = "mo_analytics_last_visit";
const HEARTBEAT_MS = 30000;
const IDLE_AFTER_MS = 60000;

export const MO_ANALYTICS_SECTIONS = new Set([
  "overview", "orders", "clients", "recipes", "recipe_recalc",
  "product_cost", "packaging", "purchases", "calendar", "scenarios",
  "brand_studio", "production", "ingredients",
]);

export const MO_ANALYTICS_TOOLS = new Set([
  "recalc_recipe", "product_cost", "brand_studio_generate",
  "workbook_save", "order_create", "order_edit_open",
  "search", "filter", "sort",
]);

function newSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

function sessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = newSessionId();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return newSessionId();
  }
}

function send(events) {
  if (!Array.isArray(events) || events.length === 0) return;
  api.post("/master-order/analytics/events", {
    session_id: sessionId(),
    events,
  }).catch(() => {
    // Product analytics must never interrupt the user's work.
  });
}

const SECTION_ALIASES = {
  "recipe-recalc": "recipe_recalc",
  "product-cost": "product_cost",
  "brand-studio": "brand_studio",
};

function safeSection(section) {
  const normalized = SECTION_ALIASES[section] || section;
  return MO_ANALYTICS_SECTIONS.has(normalized) ? normalized : null;
}

export function trackMasterOrderTool(section, tool) {
  const safe = safeSection(section);
  if (!safe || !MO_ANALYTICS_TOOLS.has(tool)) return;
  send([{ type: "tool_use", section: safe, tool }]);
}

export function trackMasterOrderError(section, code) {
  const safe = safeSection(section);
  if (!safe || !/^[A-Za-z0-9_.-]{1,32}$/.test(code || "")) return;
  send([{ type: "technical_error", section: safe, code }]);
}

export function useMasterOrderAnalytics(section, enabled = true) {
  const currentSection = safeSection(section);
  const sectionRef = useRef(currentSection);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  sectionRef.current = currentSection;

  useEffect(() => {
    if (!enabled || !currentSection) return;

    const events = [];
    try {
      if (sessionStorage.getItem(SESSION_STARTED_KEY) !== "1") {
        events.push({ type: "session_start", section: currentSection });
        sessionStorage.setItem(SESSION_STARTED_KEY, "1");

        const previousVisit = Number(localStorage.getItem(LAST_VISIT_KEY) || 0);
        if (previousVisit > 0) {
          events.push({ type: "return_visit", section: currentSection });
        }
      }

      const previousSection = sessionStorage.getItem(PREVIOUS_SECTION_KEY);
      if (
        previousSection
        && previousSection !== currentSection
        && MO_ANALYTICS_SECTIONS.has(previousSection)
      ) {
        events.push({
          type: "navigation_transition",
          from: previousSection,
          to: currentSection,
        });
      }
      events.push({ type: "section_open", section: currentSection });
      sessionStorage.setItem(PREVIOUS_SECTION_KEY, currentSection);
      localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
    } catch {
      events.push({ type: "section_open", section: currentSection });
    }
    send(events);
  }, [currentSection, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    let lastActivity = performance.now();
    const markActivity = () => { lastActivity = performance.now(); };
    const activityEvents = ["pointerdown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((name) =>
      window.addEventListener(name, markActivity, { passive: true })
    );

    const heartbeat = setInterval(() => {
      const active = (
        enabledRef.current
        && document.visibilityState === "visible"
        && document.hasFocus()
        && performance.now() - lastActivity < IDLE_AFTER_MS
      );
      const safe = safeSection(sectionRef.current);
      if (active && safe) {
        send([{ type: "heartbeat_active", section: safe, seconds: HEARTBEAT_MS / 1000 }]);
      }
    }, HEARTBEAT_MS);

    const onPageHide = () => {
      const safe = safeSection(sectionRef.current);
      if (enabledRef.current && safe) {
        send([{ type: "session_end", section: safe }]);
      }
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      clearInterval(heartbeat);
      activityEvents.forEach((name) => window.removeEventListener(name, markActivity));
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [enabled]);
}
